'use client';

import { Button } from '@tuic/web-compositions/core/Button';
import { Icon } from '@tuic/web-compositions/core/Icon';
import { TextButton } from '@tuic/web-compositions/core/TextButton';
import { CheckboxField } from '@tuic/web-compositions/shared/CheckboxField';
import { Dialog, DialogContent, DialogTrigger } from '@tuic/web-compositions/shared/Dialog';
import { InputFieldError, InputFieldLabel } from '@tuic/web-compositions/shared/InputField';
import { useState } from 'react';
import type { FC, ReactNode } from 'react';
type Option = {
  label: string;
  value: string;
};
export type ManifestMultiSelectFieldProps = {
  name: string;
  value: string[];
  onConfirm: (next: string[]) => void;
  label?: ReactNode;
  error?: string;
  options?: Option[];
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  hint?: string;
};
export const ManifestMultiSelectField: FC<ManifestMultiSelectFieldProps> = ({
  name,
  value,
  onConfirm,
  label,
  error,
  options = [],
  disabled,
  className,
  placeholder,
  confirmLabel,
  cancelLabel,
  hint
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setDraft(value);
    }
    setOpen(isOpen);
  };
  const handleToggle = (val: string, checked: boolean) => {
    setDraft(prev => checked ? [...prev, val] : prev.filter(v => v !== val));
  };
  const handleConfirm = () => {
    onConfirm(draft);
    setOpen(false);
  };
  const selectedLabels = value.map(v => options.find(o => o.value === v)?.label).filter(Boolean).join(', ');
  return <div className={className} data-sentry-component="ManifestMultiSelectField" data-sentry-source-file="ManifestMultiSelectField.tsx">
      {label && <InputFieldLabel asChild>
          <div className="mb-1">{label}</div>
        </InputFieldLabel>}

      <Dialog open={open} onOpenChange={handleOpen} data-sentry-element="Dialog" data-sentry-source-file="ManifestMultiSelectField.tsx">
        <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="ManifestMultiSelectField.tsx">
          <button type="button" disabled={disabled} className="w-full truncate rounded-md border border-stroke-secondary-40 bg-surface-white p-4 text-left text-sm text-marine-high-emphasis disabled:bg-surface-secondary-7 disabled:text-secondary-marine-48">
            {selectedLabels || placeholder || ' '}
          </button>
        </DialogTrigger>

        <DialogContent title={typeof label === 'string' ? label : undefined} disableSpacing bodyClassName="space-y-2 p-6" data-sentry-element="DialogContent" data-sentry-source-file="ManifestMultiSelectField.tsx">
          {hint && <div className="flex items-center gap-2 text-sm text-marine-medium-emphasis">
              <Icon name="info-empty" size="sm" />
              <span>{hint}</span>
            </div>}

          <div className="max-h-[50vh] space-y-3 overflow-y-auto">
            {options.map(option => <CheckboxField key={option.value} name={`${name}_${option.value}`} label={option.label} checked={draft.includes(option.value)} onChange={e => handleToggle(option.value, e.currentTarget.checked)} />)}
          </div>

          <div className="flex justify-end gap-6">
            <TextButton type="button" onClick={() => setOpen(false)} data-sentry-element="TextButton" data-sentry-source-file="ManifestMultiSelectField.tsx">
              {cancelLabel}
            </TextButton>
            <Button type="button" onClick={handleConfirm} data-sentry-element="Button" data-sentry-source-file="ManifestMultiSelectField.tsx">
              {confirmLabel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {error && <InputFieldError>{error}</InputFieldError>}
    </div>;
};